import { api, type IncomeRecord, queryKeys } from '@alpha/api-client';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { useTableQuery } from '../../hooks/useTableQuery';
import { formatDate, formatDateTime, formatLds, formatUsd, statusTone, TOKEN_LABEL } from '../../lib/format';
import { Card, Chip, EmptyState, ProgressBar, Spinner, StatCard, StatCards } from '../../ui';
import { INCOME_TABS } from './incomeTabs';

const col = createColumnHelper<IncomeRecord>();

/**
 * Elite Rank Benefits pay monthly for a fixed term, unlocked by B-Club rank:
 * Silver (B5) $50 × 10 months · Gold (B6) $100 × 15 · Diamond (B7) $300 × 20
 * plus a KIA EV9. This screen shows where the member is in that term.
 */
export function EliteRank() {
  const { query, setPage } = useTableQuery();

  const enrollment = useQuery({
    queryKey: ['elite-rank-enrollment'],
    queryFn: () => api.income.eliteRankEnrollment().then((r) => r.data),
  });

  const ledger = useQuery({
    queryKey: queryKeys.income('elite_rank', query),
    queryFn: () => api.income.list('elite_rank', query),
  });

  const columns = useMemo(
    () => [
      col.accessor('description', { header: 'Installment', cell: (i) => i.getValue() ?? '—' }),
      col.accessor('amount_usd', { header: 'Amount (USD)', cell: (i) => formatUsd(i.getValue()) }),
      col.accessor('amount_lds', { header: `Amount (${TOKEN_LABEL})`, cell: (i) => formatLds(i.getValue()) }),
      col.accessor('created_at', { header: 'Paid', cell: (i) => formatDateTime(i.getValue()) }),
    ],
    [],
  );

  const e = enrollment.data;

  return (
    <>
      <PageHeader
        title="Elite Rank Benefits"
        description="Recurring monthly rewards unlocked by Alpha Club B rank."
        tabs={INCOME_TABS}
      />

      {enrollment.isLoading ? (
        <Spinner />
      ) : !e ? (
        <EmptyState
          title="Not yet enrolled"
          description="Reach Alpha Club B5 to unlock Silver, B6 for Gold, or B7 for Diamond."
        />
      ) : (
        <>
          <StatCards>
            <StatCard label="Tier" value={e.tier.toUpperCase()} />
            <StatCard
              label="Monthly"
              value={`${formatUsd(e.monthly_amount_usd)} (${formatLds(e.monthly_amount_lds)})`}
            />
            <StatCard
              label="Installments"
              value={`${e.paid_installments} / ${e.total_installments}`}
            />
            <StatCard label="Status" value={<Chip tone={statusTone(e.status)}>{e.status}</Chip>} />
            <StatCard label="Next payout" value={formatDate(e.next_payout_at)} />
          </StatCards>

          <Card>
            <h3 className="card-title">Term progress</h3>
            <ProgressBar
              value={e.paid_installments}
              max={e.total_installments}
              label={`${e.paid_installments} of ${e.total_installments} months paid`}
            />
          </Card>
        </>
      )}

      <DataTable<IncomeRecord>
        title="Elite Rank Payouts"
        columns={columns}
        data={ledger.data?.data}
        meta={ledger.data?.meta}
        isLoading={ledger.isLoading}
        error={ledger.error as Error | null}
        emptyLabel="No Elite Rank payouts yet"
        onPageChange={setPage}
      />
    </>
  );
}
